import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { plusSecondaryContentItemContent$key } from "scmp-app/queries/__generated__/plusSecondaryContentItemContent.graphql";

import {
  Container,
  ContentLink,
  Footer,
  Header,
  Headline,
  PublishedDate,
  Section,
  StyledPlusArticleTypeTitle,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  onClick?: () => void;
  reference: plusSecondaryContentItemContent$key;
  withArticleType?: boolean;
  withPublishedDate?: boolean;
  withSections?: boolean;
  withSummary?: boolean;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
  withArticleType = true,
  withPublishedDate,
  withSections = true,
  withSummary,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusSecondaryContentItemContent on Content {
        __typename
        ...entityLink
        ...plusArticleTypeTitleContent
        ...helpersCheckIsPlusArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withActionBar: true
            withSections: true
            withSummary: true
            withPublishedDate: true
          )
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(entity);

  const isPlusArticle = checkIsPlusArticle(entity);

  // Render article type when it's plus article otherwise render section
  const handleRenderArticleTypeOrSections = () => {
    if (isPlusArticle) {
      return withArticleType && <StyledPlusArticleTypeTitle reference={entity} />;
    }
    return withSections && <Section>{providers.sections({ isDisableLink: true })}</Section>;
  };

  // Check has section or not
  return (
    <Container className={className} ref={forwardReference}>
      <Header>
        {handleRenderArticleTypeOrSections()}
        {withPublishedDate && (
          <PublishedDate>{providers.publishedDate({ format: "d MMM yyyy" })}</PublishedDate>
        )}
        <LinkHighlightArea>
          <ContentLink onClick={onClick}>
            <Headline>
              {providers.headline({
                hideTooltip: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            {withSummary && (
              <Summary>{providers.summary({ isTextOverflowEllipsis: true })}</Summary>
            )}
          </ContentLink>
        </LinkHighlightArea>
      </Header>
      <Footer>
        {providers.actionBar({
          displayDateVariant: "publishedDate",
          isBookmarkVisible: false,
          isCommentsVisible: false,
          isIconVisible: false,
        })}
      </Footer>
    </Container>
  );
};

Component.displayName = "ContentItemPlusSecondary";
export const ContentItemPlusSecondary = withHighlightedHeadline(Component);
